import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ConflictError } from '../api/client';
import type { EmployeeRosterRow, IncidentView, PointsKeyRow } from '../api/types';
import { useApp } from '../state/AppContext';
import {
  ConflictBanner, EmployeeName, EmployeePicker, EmptyRow, ErrorBanner, LocationSelect, Points, SortableHeader,
  formatDate, today, trim,
} from '../components/ui';

type SortColumn = 'entryDate' | 'employee' | 'location' | 'category' | 'label' | 'points';

/**
 * Manual entry for anything punch data cannot catch — safety, quality,
 * conduct, and rewards.
 *
 * No approval workflow in v1: submitting records the entry immediately with a
 * "Recorded" badge. The status column is deliberately generic so an
 * "Awaiting approval" state can be added later without a redesign.
 */
export function Incidents() {
  const { scopedDepartments, scopedFacilities, me, can, period, locationsFor } = useApp();
  const incidentReach = locationsFor('incidents', 'view');
  const queryClient = useQueryClient();
  const canLog = can('incidents', 'edit');

  const [from, setFrom] = useState(period.startDate);
  const [to, setTo] = useState(period.endDate);
  const [location, setLocation] = useState('all');
  const [department, setDepartment] = useState('all');
  const [employeeFilter, setEmployeeFilter] = useState('all');
  const [category, setCategory] = useState('all');
  const [sort, setSort] = useState<{ column: SortColumn; dir: 'asc' | 'desc' }>({ column: 'entryDate', dir: 'desc' });
  const [error, setError] = useState<string | null>(null);
  const [conflict, setConflict] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // The entry being edited. The form switches into edit mode for it.
  const [editing, setEditing] = useState<IncidentView | null>(null);
  const editReach = locationsFor('incidents', 'edit');
  const canEditAt = (code?: string | null) => editReach === null || (code != null && editReach.includes(code));

  const entries = useQuery({
    queryKey: ['incidents', from, to, location, employeeFilter, category, department],
    queryFn: () =>
      api.get<{ rows: IncidentView[] }>('/api/incidents', {
        from, to, location, employeeId: employeeFilter, category, department }),
  });

  const pointsKey = useQuery({ queryKey: ['points-key'], queryFn: () => api.get<PointsKeyRow[]>('/api/points-key') });
  const roster = useQuery({
    queryKey: ['roster-lite'],
    // Only people the Scorecard Scope covers (Settings → Scorecard Scope). An
    // entry for anyone else would count on no scorecard, so the pickers do
    // not offer them.
    queryFn: () => api.get<{ rows: EmployeeRosterRow[] }>('/api/employees', { take: 1000, scopeOnly: true }),
  });

  const rows = useMemo(() => {
    const list = [...(entries.data?.rows ?? [])];
    const dir = sort.dir === 'asc' ? 1 : -1;
    list.sort((a, b) => {
      switch (sort.column) {
        case 'employee': return a.employee.name.localeCompare(b.employee.name) * dir;
        case 'location': return (a.employee.locationCode ?? '').localeCompare(b.employee.locationCode ?? '') * dir;
        case 'category': return a.category.localeCompare(b.category) * dir;
        case 'label': return a.label.localeCompare(b.label) * dir;
        case 'points': return (a.points - b.points) * dir;
        default: return a.entryDate.localeCompare(b.entryDate) * dir;
      }
    });
    return list;
  }, [entries.data, sort]);

  const toggleSort = (column: string) =>
    setSort((s) => ({ column: column as SortColumn, dir: s.column === column && s.dir === 'desc' ? 'asc' : 'desc' }));

  // Categories come from the live Points Key, so a category added in Settings
  // shows up here without a code change. Attendance is excluded from the
  // filter list because those points come from Daily Summary instead.
  const categories = useMemo(
    () => [...new Set((pointsKey.data ?? []).filter((p) => p.status !== 'Retired').map((p) => p.category))],
    [pointsKey.data],
  );

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['incidents'] });
    queryClient.invalidateQueries({ queryKey: ['scorecard'] });
    queryClient.invalidateQueries({ queryKey: ['dashboard'] });
  };

  const flash = (message: string) => {
    setNotice(message);
    setTimeout(() => setNotice(null), 4000);
  };

  // A 409 gets the conflict banner rather than an error: someone else changed
  // the entry, and the fix is to reload it.
  const handleError = (e: Error) => (e instanceof ConflictError ? setConflict(e.message) : setError(e.message));

  const remove = useMutation({
    mutationFn: (r: IncidentView) => api.del(`/api/incidents/${r.incidentEntryId}?rowVersionNo=${r.rowVersionNo}`),
    onSuccess: (_data, r) => {
      if (editing?.incidentEntryId === r.incidentEntryId) setEditing(null);
      invalidate();
      flash(`Deleted — ${r.label} for ${r.employee.name}`);
    },
    onError: handleError,
  });

  const confirmDelete = (r: IncidentView) => {
    const pts = r.points > 0 ? `+${trim(r.points)}` : trim(r.points);
    if (window.confirm(
      `Delete "${r.label}" (${pts} pts) for ${r.employee.name} on ${formatDate(r.entryDate)}?\n\n`
      + 'It comes off their score and out of this list. The change history keeps a copy.'))
      remove.mutate(r);
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Incident &amp; Rewards</div>
          <div className="page-subtitle">
            {canLog
              ? 'Log anything the punch data cannot catch — safety, quality, conduct and rewards'
              : 'Read-only for your role — entries are logged by Location Managers and HR/Admin'}
          </div>
        </div>
      </div>

      {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}
      {conflict && (
        <ConflictBanner message={conflict} onReload={() => { setConflict(null); setEditing(null); invalidate(); }} />
      )}

      <div style={{ display: 'grid', gridTemplateColumns: canLog ? '380px 1fr' : '1fr', gap: 20 }}>
        {canLog && (
          <EntryForm
            // Remounting when switching between a new entry and an edit resets every field.
            key={editing ? `edit-${editing.incidentEntryId}` : 'new'}
            pointsKey={pointsKey.data ?? []}
            employees={roster.data?.rows ?? []}
            enteredBy={me.displayName}
            editing={editing}
            onDoneEditing={() => setEditing(null)}
            onSaved={(message) => { invalidate(); if (message) flash(message); }}
            onError={handleError}
          />
        )}

        <div>
          <div className="section-label" style={{ marginBottom: 2 }}>INCIDENT &amp; REWARD ENTRIES</div>
          <div className="meta" style={{ marginBottom: 10 }}>
            {rows.length} entries · {incidentReach === null ? 'all locations'
              : incidentReach.length === 1 ? 'your location'
              : `${incidentReach.length} locations`}
          </div>
          {notice && <div className="pill pill--good" style={{ display: 'inline-block', marginBottom: 10 }}>{notice}</div>}

          <div className="filter-row">
            <input type="date" className="control" value={from} onChange={(e) => setFrom(e.target.value)} aria-label="From" />
            <span className="meta">to</span>
            <input type="date" className="control" value={to} onChange={(e) => setTo(e.target.value)} aria-label="To" />
            <select className="control" style={{ flex: 1, minWidth: 150 }} value={employeeFilter}
              onChange={(e) => setEmployeeFilter(e.target.value)} aria-label="Employee">
              <option value="all">All employees</option>
              {(roster.data?.rows ?? []).map((r) => (
                <option key={r.employeeId} value={r.employeeId}>{r.displayName}</option>
              ))}
            </select>
          <select className="control" style={{ maxWidth: 220 }} value={department} onChange={(e) => setDepartment(e.target.value)} aria-label="Department">
            <option value="all">All departments</option>
            <option value="__none__">— No department set —</option>
            {scopedDepartments.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
            <LocationSelect facilities={scopedFacilities} allowed={incidentReach} value={location} onChange={setLocation} />
            <select className="control" value={category} onChange={(e) => setCategory(e.target.value)} aria-label="Category">
              <option value="all">All categories</option>
              {categories.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>

          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <SortableHeader label="DATE" column="entryDate" sort={sort} onSort={toggleSort} />
                  <th className="num">ID</th>
                  <SortableHeader label="EMPLOYEE" column="employee" sort={sort} onSort={toggleSort} />
                  <SortableHeader label="LOCATION" column="location" sort={sort} onSort={toggleSort} />
                  <SortableHeader label="CATEGORY" column="category" sort={sort} onSort={toggleSort} />
                  <SortableHeader label="DETAIL" column="label" sort={sort} onSort={toggleSort} />
                  <SortableHeader label="PTS" column="points" sort={sort} onSort={toggleSort} className="num" />
                  <th>STATUS</th>
                  {canLog && <th aria-label="Actions" />}
                </tr>
              </thead>
              <tbody>
                {entries.isLoading && <EmptyRow colSpan={canLog ? 9 : 8}>Loading entries…</EmptyRow>}
                {!entries.isLoading && rows.length === 0 && (
                  <EmptyRow colSpan={canLog ? 9 : 8}>
                    Nothing logged in this window yet. Attendance points come from Daily Summary; this log covers
                    everything the punch data cannot see.
                  </EmptyRow>
                )}
                {rows.map((r) => (
                  <tr key={r.incidentEntryId} className={r.isLocked ? 'locked' : undefined}
                    style={editing?.incidentEntryId === r.incidentEntryId ? { background: 'var(--blue-tint)' } : undefined}>
                    <td>{formatDate(r.entryDate)}</td>
                    <td className="num">{r.employee.employeeId}</td>
                    <td className="strong"><EmployeeName employee={r.employee} /></td>
                    <td>{r.employee.locationCode ?? '—'}</td>
                    <td>{r.category}</td>
                    <td>{r.label}</td>
                    <td className="num"><Points value={r.points} /></td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <span className="pill pill--good">
                        {r.status === 'recorded' ? 'Recorded' : r.status}
                      </span>
                      {r.outsideScope && (
                        <span
                          className="pill"
                          style={{ marginLeft: 6, background: 'var(--rule)', color: 'var(--muted)' }}
                          title={`${r.employee.name} is outside Settings → Scorecard Scope, so this entry counts on no scorecard. Delete it if it was logged by mistake.`}
                        >
                          Outside scope
                        </span>
                      )}
                    </td>
                    {canLog && (
                      <td style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                        {!r.isLocked && canEditAt(r.employee.locationCode) && (
                          <>
                            <button className="btn btn--sm" onClick={() => setEditing(r)}>Edit</button>{' '}
                            <button className="btn btn--sm btn--reject" disabled={remove.isPending}
                              onClick={() => confirmDelete(r)}>Delete</button>
                          </>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

function EntryForm({
  pointsKey, employees, enteredBy, editing, onDoneEditing, onSaved, onError,
}: {
  pointsKey: PointsKeyRow[];
  employees: EmployeeRosterRow[];
  enteredBy: string;
  /** When set, the form edits this entry instead of logging a new one. */
  editing: IncidentView | null;
  onDoneEditing: () => void;
  onSaved: (message?: string) => void;
  onError: (e: Error) => void;
}) {
  const { period } = useApp();
  const [employeeId, setEmployeeId] = useState<number | null>(editing?.employee.employeeId ?? null);
  // Bumped after a save to remount the picker, which clears what was typed.
  const [pickerKey, setPickerKey] = useState(0);
  const [date, setDate] = useState(editing?.entryDate ?? today());
  const [category, setCategory] = useState(editing?.category ?? '');
  const [label, setLabel] = useState(editing?.label ?? '');
  const [notes, setNotes] = useState(editing?.notes ?? '');
  const [saved, setSaved] = useState<string | null>(null);

  // An entry being edited keeps its own infraction selectable even if the
  // Points Key has since retired it; otherwise the list would quietly swap it.
  const active = pointsKey.filter((p) => p.status !== 'Retired'
    || (editing != null && p.category === editing.category && p.label === editing.label));
  const categories = useMemo(() => [...new Set(active.map((p) => p.category))], [pointsKey]);
  const options = useMemo(() => active.filter((p) => p.category === category), [pointsKey, category]);

  // Default to Safety when it exists: attendance points come from Daily
  // Summary, so landing on Attendance points the form at the wrong screen.
  // Falls back to whatever the live Points Key has, because a Settings edit
  // can rename or remove any category.
  useEffect(() => {
    if (!category && categories.length > 0) setCategory(categories.find((c) => c === 'Safety') ?? categories[0]);
  }, [categories, category]);

  useEffect(() => {
    if (options.length > 0 && !options.some((o) => o.label === label)) setLabel(options[0].label);
  }, [options, label]);

  const selected = options.find((o) => o.label === label);

  const submit = useMutation({
    mutationFn: () =>
      editing
        ? api.put<{ points: number }>(`/api/incidents/${editing.incidentEntryId}`, {
            entryDate: date, category, label, notes: notes || null, rowVersionNo: editing.rowVersionNo,
          })
        : api.post<{ points: number }>('/api/incidents', {
            employeeId, entryDate: date, category, label, notes: notes || null,
          }),
    onSuccess: () => {
      if (editing) {
        onSaved(`Updated — ${label} for ${editing.employee.name}`);
        onDoneEditing();
        return;
      }
      setSaved(`Recorded — ${label}`);
      setNotes('');
      setEmployeeId(null);
      setPickerKey((k) => k + 1);
      onSaved();
      setTimeout(() => setSaved(null), 4000);
    },
    onError: (e) => onError(e),
  });

  const disabled = !employeeId || !category || !label || submit.isPending;

  return (
    <div className="card" style={editing ? { borderColor: 'var(--blue-border)' } : undefined}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {editing && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div className="section-label">EDITING AN ENTRY</div>
            <button className="btn btn--sm" onClick={onDoneEditing}>Cancel</button>
          </div>
        )}
        <div>
          <label className="field-label" htmlFor="inc-employee">EMPLOYEE</label>
          {editing ? (
            <>
              <div id="inc-employee" className="control" style={{ width: '100%', background: 'var(--surface-muted)' }}>
                {editing.employee.name} ({editing.employee.employeeId})
              </div>
              <div className="meta" style={{ marginTop: 4 }}>
                To change the person, delete this entry and enter it again.
              </div>
            </>
          ) : (
            <EmployeePicker
              key={pickerKey}
              id="inc-employee"
              people={employees}
              value={employeeId}
              onChange={setEmployeeId}
            />
          )}
        </div>

        <div>
          <label className="field-label" htmlFor="inc-date">DATE</label>
          <input id="inc-date" type="date" className="control" style={{ width: '100%' }}
            value={date} max={today()} onChange={(e) => setDate(e.target.value)}
            min={editing && editing.entryDate < period.startDate ? editing.entryDate : period.startDate} />
          <div style={{ fontSize: 10.5, color: 'var(--muted)', marginTop: 3 }}>
            Must be within {period.name} ({period.startDate} to today)
          </div>
        </div>

        <div>
          <label className="field-label" htmlFor="inc-category">CATEGORY</label>
          <select id="inc-category" className="control" style={{ width: '100%' }}
            value={category} onChange={(e) => setCategory(e.target.value)}>
            {categories.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>

        <div>
          <label className="field-label" htmlFor="inc-label">INFRACTION / REWARD</label>
          <div className="meta" style={{ marginBottom: 6 }}>
            Rewards are counted exactly as entered. Six points per quarter is the intended ceiling, but it is
            guidance for you rather than a limit the system applies — nothing is silently discarded.
          </div>
          <select id="inc-label" className="control" style={{ width: '100%' }}
            value={label} onChange={(e) => setLabel(e.target.value)}>
            {options.map((o) => <option key={o.pointsKeyEntryId} value={o.label}>{o.label}</option>)}
          </select>
        </div>

        <div>
          <label className="field-label">POINTS (auto-filled)</label>
          {/* Read-only on purpose: the value the server stores is resolved
              against the Points Key version in effect on the entry's date. */}
          <div
            style={{
              padding: '8px 10px', border: '1px solid var(--divider)', borderRadius: 'var(--radius-control)',
              fontSize: 14, fontWeight: 700, background: 'var(--surface-muted)',
              color: !selected ? 'var(--muted)' : selected.points < 0 ? 'var(--red)' : selected.points > 0 ? 'var(--green)' : 'var(--muted)',
            }}
          >
            {!selected ? '—' : selected.points === 0 ? '0' : selected.points > 0 ? `+${trim(selected.points)}` : trim(selected.points)}
          </div>
          {selected?.note && <div style={{ fontSize: 10.5, color: 'var(--note)', marginTop: 4 }}>{selected.note}</div>}
        </div>

        <div>
          <label className="field-label" htmlFor="inc-notes">NOTES</label>
          <textarea id="inc-notes" className="control" style={{ width: '100%', minHeight: 70, resize: 'vertical' }}
            placeholder="Optional details…" value={notes} onChange={(e) => setNotes(e.target.value)} />
        </div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: 4, borderTop: '1px solid var(--divider)' }}>
          <div style={{ fontSize: 11.5, color: 'var(--muted)' }}>
            {editing ? 'Editing as' : 'Entered by'} <strong style={{ color: 'var(--text-secondary)' }}>{enteredBy}</strong>
          </div>
          <button className="btn btn--primary" disabled={disabled} onClick={() => submit.mutate()}>
            {submit.isPending ? 'Saving…' : editing ? 'Save changes' : 'Log entry'}
          </button>
        </div>

        {saved && <div className="pill pill--good" style={{ alignSelf: 'flex-start' }}>{saved}</div>}
      </div>
    </div>
  );
}
